/**
 * Petstyle24 – globale Grundlage (Build-Plan v2, Phase A). Keine Abhängigkeiten.
 * Progressive Enhancement: Ohne JavaScript funktionieren Links, Formulare und Anker nativ.
 *
 * window.Petstyle24
 *   config               Routen und Systemtexte aus #ps-config (nur Shopify-Routen, keine Preise)
 *   on(name, handler)    Event-Bus über DOM-Events „ps:<name>“; gibt eine Abmeldefunktion zurück
 *   emit(name, detail)
 *   fetchSection(url, id)        Section Rendering API (?section_id=) → HTML-Text
 *   fetchSections(url, ids)      Section Rendering API (?sections=)   → { id: HTML }
 *   parseHTML(html)      DOMParser; nur für Section-HTML aus dem eigenen Shop (gleiche Herkunft)
 *   sameOriginUrl(value) URL-Objekt oder null, wenn nicht gleiche Herkunft
 *   announce(text)       Statusmeldung in die Live-Region #ps-live
 *   dialog.open(dialog, trigger) / dialog.close(dialog) / dialog.bind(dialog)
 *     nach Touch/Stift: Startfokus ohne sichtbaren Ring (data-ps-focus-quiet, M1.2)
 *   prefersReducedMotion()
 *
 * Scroll-Lock: kein JavaScript-Lock. Einziger Mechanismus ist
 * html:has(dialog[scroll-lock][open]) (critical.css, component-dialog.css; #33, 05c §15C).
 *
 * Anker (#18, #42): auf derselben Seite sanft, bei Seitenwechsel sofort, bei reduced motion
 * immer sofort. Der Header-Offset kommt aus scroll-padding-top (critical.css).
 *
 * Sicherheit (05b §19, 05c §18): kein eval, kein innerHTML, keine Konsolen-Ausgaben;
 * Netzwerkzugriffe nur auf die eigene Herkunft; Ziele aus dem Hash werden nur per
 * getElementById aufgelöst, nie als Selektor oder Weiterleitung verwendet.
 */
(() => {
  'use strict';

  if (window.Petstyle24 && window.Petstyle24.ready) return;

  /* ---------- Konfiguration ---------- */
  const readConfig = () => {
    const empty = { routes: {}, strings: {} };
    const element = document.getElementById('ps-config');
    if (!element) return empty;
    try {
      const parsed = JSON.parse(element.textContent);
      return {
        routes: parsed && typeof parsed.routes === 'object' ? parsed.routes : {},
        strings: parsed && typeof parsed.strings === 'object' ? parsed.strings : {},
      };
    } catch (error) {
      return empty;
    }
  };

  const config = readConfig();

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Event-Bus ---------- */
  const emit = (name, detail) => {
    document.dispatchEvent(new CustomEvent(`ps:${name}`, { detail }));
  };

  const on = (name, handler) => {
    const listener = (event) => handler(event.detail, event);
    document.addEventListener(`ps:${name}`, listener);
    return () => document.removeEventListener(`ps:${name}`, listener);
  };

  /* ---------- Netzwerk: ausschließlich gleiche Herkunft ---------- */
  const sameOriginUrl = (value) => {
    if (typeof value !== 'string' || value === '') return null;
    try {
      const url = new URL(value, window.location.href);
      return url.origin === window.location.origin ? url : null;
    } catch (error) {
      return null;
    }
  };

  const request = async (url, accept) => {
    const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: accept } });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response;
  };

  const fetchSection = async (path, sectionId) => {
    const url = sameOriginUrl(path);
    if (!url || typeof sectionId !== 'string' || sectionId === '') throw new Error('Ungültige Anfrage');
    url.searchParams.set('section_id', sectionId);
    const response = await request(url, 'text/html');
    return response.text();
  };

  const fetchSections = async (path, sectionIds) => {
    const url = sameOriginUrl(path);
    if (!url || !Array.isArray(sectionIds) || sectionIds.length === 0) throw new Error('Ungültige Anfrage');
    url.searchParams.set('sections', sectionIds.join(','));
    const response = await request(url, 'application/json');
    return response.json();
  };

  const parseHTML = (html) => new DOMParser().parseFromString(String(html), 'text/html');

  /* ---------- Live-Region ---------- */
  let announceTimer = 0;

  const announce = (message) => {
    const region = document.getElementById('ps-live');
    if (!region || !message) return;
    window.clearTimeout(announceTimer);
    region.textContent = '';
    announceTimer = window.setTimeout(() => {
      region.textContent = String(message);
    }, 60);
  };

  /* ---------- Dialoge (natives <dialog>) ---------- */
  const CLOSE_FALLBACK_MS = 450;
  const triggers = new WeakMap();
  const boundDialogs = new WeakSet();

  // Startfokus nach Touch-Öffnen ohne sichtbaren Ring (M1.2).
  // WebKit (iOS/iPadOS) fokussiert beim Antippen keine Links und Buttons. Den Startfokus, den
  // showModal() danach per Skript setzt, wertet WebKit deshalb nicht als Klick-Fokus, sondern als
  // :focus-visible – der Fokusring (#11) erschien als Kreis um das X. Nur nach Touch/Stift wird
  // dieser Startfokus markiert (component-dialog.css), bis eine Taste gedrückt wird oder der Fokus
  // wandert. Maus und Tastatur bleiben unverändert; Fokus, Esc und Fokus-Rückgabe ebenso.
  const QUIET_FOCUS = 'data-ps-focus-quiet';
  let lastPointerType = '';
  document.addEventListener('pointerdown', (event) => {
    lastPointerType = event.pointerType || '';
  }, true);
  document.addEventListener('keydown', () => {
    lastPointerType = '';
  }, true);

  const quietInitialFocus = (dialog) => {
    const target = document.activeElement;
    if (!target || target === dialog || !dialog.contains(target)) return;
    target.setAttribute(QUIET_FOCUS, '');
    const release = () => {
      target.removeAttribute(QUIET_FOCUS);
      target.removeEventListener('blur', release);
      document.removeEventListener('keydown', release, true);
    };
    target.addEventListener('blur', release);
    document.addEventListener('keydown', release, true);
  };

  const restoreFocus = (dialog) => {
    const trigger = triggers.get(dialog);
    triggers.delete(dialog);
    if (trigger && trigger.isConnected && typeof trigger.focus === 'function') {
      trigger.focus({ preventScroll: true });
    }
  };

  const finishClose = (dialog) => {
    dialog.classList.remove('is-closing');
    if (dialog.open) dialog.close();
  };

  const closeDialog = (dialog) => {
    if (!dialog || !dialog.open || dialog.classList.contains('is-closing')) return;
    if (prefersReducedMotion()) {
      finishClose(dialog);
      return;
    }

    dialog.classList.add('is-closing');
    if (window.getComputedStyle(dialog).animationName === 'none') {
      finishClose(dialog);
      return;
    }

    let finished = false;
    const complete = () => {
      if (finished) return;
      finished = true;
      dialog.removeEventListener('animationend', onAnimationEnd);
      finishClose(dialog);
    };
    const onAnimationEnd = (event) => {
      if (event.target === dialog && !event.pseudoElement) complete();
    };
    dialog.addEventListener('animationend', onAnimationEnd);
    window.setTimeout(complete, CLOSE_FALLBACK_MS);
  };

  const bindDialog = (dialog) => {
    if (!dialog || boundDialogs.has(dialog)) return;
    boundDialogs.add(dialog);

    // Esc: animiert schließen statt sofort
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDialog(dialog);
    });

    // Klick auf den Scrim trifft das <dialog> selbst (das Panel füllt den Dialog aus)
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog(dialog);
    });

    dialog.addEventListener('close', () => {
      dialog.classList.remove('is-closing');
      restoreFocus(dialog);
      emit('dialog:close', { dialog });
    });
  };

  const openDialog = (dialog, trigger) => {
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    bindDialog(dialog);
    triggers.set(dialog, trigger || document.activeElement);
    dialog.classList.remove('is-closing');
    if (!dialog.open) {
      dialog.showModal();
      if (lastPointerType === 'touch' || lastPointerType === 'pen') quietInitialFocus(dialog);
    }
    emit('dialog:open', { dialog });
    return true;
  };

  /* ---------- Anker mit Header-Offset ---------- */
  const FOCUSABLE = 'a[href], button, input, select, textarea, summary, [tabindex]';

  const targetFromHash = (hash) => {
    if (typeof hash !== 'string' || hash.length < 2) return null;
    let id;
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch (error) {
      return null;
    }
    return document.getElementById(id);
  };

  const focusTarget = (target) => {
    if (!target.matches(FOCUSABLE)) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  };

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target instanceof Element ? event.target.closest('a[href*="#"]') : null;
    if (!link || (link.target && link.target !== '_self')) return;

    const url = sameOriginUrl(link.getAttribute('href'));
    if (!url || url.pathname !== window.location.pathname || url.search !== window.location.search) return;

    const target = targetFromHash(url.hash);
    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    if (window.location.hash !== url.hash) window.history.pushState(null, '', url.hash);
    focusTarget(target);
  });

  // Seitenwechsel mit Anker: sofort. Nach dem Laden einmal nachkorrigieren, falls sich das
  // Layout durch Schriften oder Bilder verschoben hat – nur solange niemand selbst gescrollt hat.
  let userMoved = false;
  const markUserMove = () => {
    userMoved = true;
  };
  ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach((type) => {
    window.addEventListener(type, markUserMove, { passive: true, once: true });
  });
  window.addEventListener(
    'load',
    () => {
      if (userMoved) return;
      const target = targetFromHash(window.location.hash);
      if (target) target.scrollIntoView({ behavior: 'auto', block: 'start' });
    },
    { once: true }
  );

  /* ---------- Öffentliche Schnittstelle ---------- */
  window.Petstyle24 = Object.assign(window.Petstyle24 || {}, {
    ready: true,
    config,
    on,
    emit,
    fetchSection,
    fetchSections,
    parseHTML,
    sameOriginUrl,
    announce,
    dialog: { open: openDialog, close: closeDialog, bind: bindDialog },
    prefersReducedMotion,
  });

  emit('ready');
})();
